import { useLayoutEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ROUTES } from '../../lib/config';
import { cn } from '../../lib/cn';
import { formatNumber } from '../../lib/format';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Callout, Panel, PanelHeader, PanelSpacer, PanelTitle } from '../../components/ui/Panel';
import { formatLogTime, statusPresentation } from './scriptStatus';
import { useCampaignLogs } from './useCampaignLogs';

/** Within this many pixels of the bottom counts as "following the latest line". */
const STICKY_THRESHOLD_PX = 32;

const LEVEL_CLASSES = {
  success: 'text-[#7ee787]',
  error: 'text-[#ff7b72]',
  warn: 'text-[#e3b341]',
  info: 'text-[#79c0ff]'
};

/**
 * Monitoring Campaign: one campaign's status and live log, for the campaign chosen on
 * Running Bulk Scripts. Keyed by the campaign reference so switching campaigns starts
 * a fresh log rather than mixing two trails.
 */
export function CampaignMonitorPage() {
  const { campaignRef } = useParams();
  return <CampaignMonitor key={campaignRef} campaignRef={campaignRef} />;
}

function CampaignMonitor({ campaignRef }) {
  const { campaign, lines, truncatedBefore, error, notFound, isLoading } = useCampaignLogs(campaignRef);

  const scrollRef = useRef(null);
  const [following, setFollowing] = useState(true);

  // Follow the newest line unless the reader has scrolled up to inspect older ones.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && following) el.scrollTop = el.scrollHeight;
  }, [lines, following]);

  function handleScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= STICKY_THRESHOLD_PX;
    if (atBottom !== following) setFollowing(atBottom);
  }

  function jumpToLatest() {
    setFollowing(true);
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }

  if (notFound) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-3">
        <BackLink />
        <Callout tone="warning" icon="fa-circle-question">
          This campaign could not be found. It may have been deleted.
        </Callout>
      </div>
    );
  }

  const status = campaign ? statusPresentation(campaign.status) : null;
  const counts = campaign?.counts;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-3">
      <BackLink />

      <header className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h1 className="min-w-0 text-xl font-bold tracking-tight text-ink-900">
          Monitoring Campaign: {campaign ? campaign.ownerName : '…'}
        </h1>
        {status ? (
          <Badge tone={status.tone} icon={status.icon}>
            {status.label}
          </Badge>
        ) : null}
      </header>

      {campaign ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-600">
          <span>
            <i className="fa-solid fa-file-csv mr-1.5 text-ink-400" aria-hidden="true" />
            {campaign.dataFile}
          </span>
          {counts ? (
            <span className="flex flex-wrap gap-x-3">
              <span>Sent <strong className="text-success-600">{formatNumber(counts.sent)}</strong></span>
              <span>Failed <strong className="text-danger-600">{formatNumber(counts.failed)}</strong></span>
              <span>Pending <strong className="text-ink-800">{formatNumber(counts.pending)}</strong></span>
              <span>Total <strong className="text-ink-800">{formatNumber(counts.total)}</strong></span>
            </span>
          ) : null}
          {campaign.status === 'stopped' && campaign.stoppedBy ? (
            <span className="text-muted">Stopped by {campaign.stoppedBy}</span>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <Callout tone="warning" icon="fa-triangle-exclamation">
          Could not refresh the log ({error.message}). Retrying automatically.
        </Callout>
      ) : null}

      <Panel className="relative">
        <PanelHeader>
          <PanelTitle icon="fa-terminal">Campaign log</PanelTitle>
          <PanelSpacer />
          <span className="text-xs text-muted">
            {following ? 'Following latest' : 'Paused scrolling'} · recipient addresses are masked
          </span>
        </PanelHeader>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          role="log"
          aria-live="polite"
          aria-label="Campaign log"
          className={cn(
            'h-[65vh] min-h-[320px] overflow-auto bg-[#0d1117] px-4 py-3',
            'font-mono text-[13px] leading-relaxed text-[#c9d1d9]'
          )}
        >
          {isLoading ? (
            <p className="text-[#8b949e]">
              <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden="true" />
              Loading log…
            </p>
          ) : lines.length === 0 ? (
            <p className="text-[#8b949e]">
              No log entries yet. Lines appear here as emails are sent.
            </p>
          ) : (
            <>
              {truncatedBefore ? (
                <p className="mb-1 text-[#8b949e]">… showing the most recent entries</p>
              ) : null}
              {lines.map((line) => (
                <div key={line.id} className="flex gap-3 whitespace-pre-wrap break-all">
                  <span className="shrink-0 select-none text-[#8b949e]">
                    {formatLogTime(line.time) || '--:--:--'}
                  </span>
                  <span className={LEVEL_CLASSES[line.level] ?? ''}>{line.message}</span>
                </div>
              ))}
            </>
          )}
        </div>

        {!following && lines.length > 0 ? (
          <Button
            size="sm"
            variant="primary"
            icon="fa-arrow-down"
            className="absolute right-4 bottom-4 shadow-md"
            onClick={jumpToLatest}
          >
            Jump to latest
          </Button>
        ) : null}
      </Panel>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to={ROUTES.scripts}
      className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-brand-600 no-underline hover:underline"
    >
      <i className="fa-solid fa-arrow-left" aria-hidden="true" />
      Running Bulk Scripts
    </Link>
  );
}
